import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { GridHelperService } from 'ui-components/services/grid-helper-service';

import { usePusherEvent } from '../../../../hooks/use-pusher-event';
import { useHideExternallyChangedOrders } from '../../../../hooks/use-user-setting';
import { BATCH_PROGRESS, ORDER_STATUS_UPDATED } from '../../../../services/pusher-service/pusher-contract-messages';
import { addOutdatedOrders } from '../../../../store/orders/selected-orders/orders.selected-orders.actions';
import { getSalesOrderStatusFromId, getSalesOrderStatusFromInt } from '../../../../enums/sales-order-status';

export const useExternalChangeListener = (outdatedIds, data, orderGridStatusId) => {
  const dispatch = useDispatch();
  const shouldHideExternallyChangedOrders = useHideExternallyChangedOrders();

  const onStatusUpdated = useCallback(
    (newOutdatedIds) => {
      const newState = {};
      newOutdatedIds.forEach((orderId) => {
        newState[orderId] = true;
      });
      dispatch(addOutdatedOrders(newState));
    },
    [dispatch],
  );

  const onPusherBatchProgress = useCallback(
    (pusherMessage) => {
      const currentOrderGridStatus = getSalesOrderStatusFromId(orderGridStatusId);

      const { orderStatus } = pusherMessage;
      const orderStatusChanged = currentOrderGridStatus !== getSalesOrderStatusFromInt(orderStatus?.status);
      if (orderStatus?.orderId && orderStatusChanged) {
        onStatusUpdated([`${orderStatus.orderId}`]);
      }
    },
    [orderGridStatusId, onStatusUpdated],
  );
  usePusherEvent(BATCH_PROGRESS, onPusherBatchProgress);

  const onPusherOrderStatusUpdated = useCallback(
    (pusherMessage) => {
      const { orderStatus } = pusherMessage;
      if (!Array.isArray(orderStatus)) {
        return;
      }
      const currentOrderGridStatus = getSalesOrderStatusFromId(orderGridStatusId);
      const orderIds = orderStatus
        .filter((status) => status.orderId && status.status && currentOrderGridStatus !== getSalesOrderStatusFromInt(status.status))
        .map((status) => `${status.orderId}`);

      if (orderIds.length > 0) {
        onStatusUpdated(orderIds);
      }
    },
    [orderGridStatusId, onStatusUpdated],
  );
  usePusherEvent(ORDER_STATUS_UPDATED, onPusherOrderStatusUpdated);

  return useMemo(() => {
    return !shouldHideExternallyChangedOrders
      ? data
      : data.filter((datum) => {
          const primaryRow = GridHelperService.getPrimaryRow(datum);
          return !outdatedIds[primaryRow.id];
        });
  }, [data, outdatedIds, shouldHideExternallyChangedOrders]);
};
